import React, { useMemo, useState } from 'react';
import {
  MONTH_NAMES,
  WEEKDAYS_MON,
  WEEKDAYS_MON_SHORT,
  addMonths,
  isBetween,
  monthMatrix,
  parseISO,
  sameISO,
  todayISO,
} from './dateutils';

/**
 * The calendar grid shared by the Date, Date-range and Date-&-time pickers.
 * Monday-first; today ringed, selection filled, range banded, other-month +
 * out-of-bounds days muted. Colours/sizes are the design tokens from the
 * "Date & time — picking states" spec.
 */

const ACCENT = '#2f6fdb';

interface Props {
  mode?: 'single' | 'range';
  /** single mode */
  selected?: string | null;
  /** range mode */
  rangeStart?: string | null;
  rangeEnd?: string | null;
  min?: string;
  max?: string;
  onSelect: (iso: string) => void;
  /** compact = the DateTime combined popover (smaller cells, single-letter days) */
  compact?: boolean;
  /** footer content (Today/Clear line or preset chips) */
  footer?: React.ReactNode;
}

const Chevron: React.FC<{ dir: 'left' | 'right'; size: number }> = ({
  dir,
  size,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
  </svg>
);

const CalendarPanel: React.FC<Props> = ({
  mode = 'single',
  selected,
  rangeStart,
  rangeEnd,
  min,
  max,
  onSelect,
  compact = false,
  footer,
}) => {
  // View month: seed from the current selection (or today), then navigable.
  const seed =
    parseISO(selected || rangeStart) ?? parseISO(todayISO()) ?? new Date();
  const [view, setView] = useState({
    year: seed.getFullYear(),
    month: seed.getMonth(),
  });

  const cells = useMemo(
    () => monthMatrix(view.year, view.month),
    [view.year, view.month],
  );
  const today = todayISO();

  const cellSize = compact ? 29 : 34;
  const fontSize = compact ? 12 : 13;
  const radius = compact ? 8 : 9;
  const navSize = compact ? 26 : 28;
  const weekdays = compact ? WEEKDAYS_MON_SHORT : WEEKDAYS_MON;

  const disabled = (iso: string): boolean =>
    (!!min && iso < min) || (!!max && iso > max);

  const go = (delta: number) => {
    const next = addMonths(view.year, view.month, delta);
    setView(next);
  };

  const cellStyle = (
    iso: string,
    inMonth: boolean,
    col: number,
  ): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: cellSize,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize,
      borderRadius: radius,
      cursor: 'pointer',
      userSelect: 'none',
    };
    if (disabled(iso)) {
      return { ...base, color: '#c4ccd6', cursor: 'not-allowed' };
    }
    if (!inMonth) return { ...base, color: '#c4ccd6' };

    if (mode === 'range') {
      const isStart = sameISO(iso, rangeStart);
      const isEnd = sameISO(iso, rangeEnd);
      const between = isBetween(iso, rangeStart, rangeEnd);
      if (isStart || isEnd) {
        // Endpoint: filled accent, rounded on its outer side (both when it's
        // the only endpoint so it reads as a single chip).
        let br = radius;
        if (rangeStart && rangeEnd && !sameISO(rangeStart, rangeEnd)) {
          br = 0;
          const rad = `${radius}px`;
          const style: React.CSSProperties = {
            ...base,
            background: ACCENT,
            color: '#fff',
            fontWeight: 700,
          };
          style.borderRadius = isStart
            ? `${rad} 0 0 ${rad}`
            : `0 ${rad} ${rad} 0`;
          return style;
        }
        return {
          ...base,
          background: ACCENT,
          color: '#fff',
          fontWeight: 700,
          borderRadius: br,
        };
      }
      if (between) {
        // Continuous band; round only at row edges so the run stays flush.
        const rad = `${radius}px`;
        return {
          ...base,
          background: '#eaf1fc',
          color: ACCENT,
          borderRadius:
            col === 0 ? `${rad} 0 0 ${rad}` : col === 6 ? `0 ${rad} ${rad} 0` : 0,
        };
      }
    } else if (sameISO(iso, selected)) {
      return {
        ...base,
        background: ACCENT,
        color: '#fff',
        fontWeight: 700,
      };
    }

    if (iso === today) {
      return {
        ...base,
        color: ACCENT,
        fontWeight: 700,
        boxShadow: 'inset 0 0 0 1.5px #9dc0f5',
      };
    }
    return { ...base, color: '#39414f' };
  };

  return (
    <div style={{ padding: compact ? '12px 13px 13px' : '12px 13px 13px' }}>
      {/* header: ‹ Month YYYY › */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        {(['left', 'right'] as const).map((dir, i) => {
          const btn = (
            <button
              key={dir}
              type="button"
              onClick={() => go(dir === 'left' ? -1 : 1)}
              style={{
                width: navSize,
                height: navSize,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6a7383',
                cursor: 'pointer',
                border: '1px solid #e6e9ee',
                background: '#fff',
              }}
              aria-label={dir === 'left' ? 'Previous month' : 'Next month'}
            >
              <Chevron dir={dir} size={compact ? 13 : 14} />
            </button>
          );
          return i === 0 ? (
            <React.Fragment key="l">
              {btn}
              <span
                style={{
                  fontSize: compact ? 13 : 13.5,
                  fontWeight: 800,
                  color: '#1f2a3d',
                }}
              >
                {MONTH_NAMES[view.month]} {view.year}
              </span>
            </React.Fragment>
          ) : (
            btn
          );
        })}
      </div>

      {/* weekday header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7,1fr)',
          marginBottom: 2,
        }}
      >
        {weekdays.map((w, i) => (
          <span
            key={i}
            style={{
              height: compact ? 20 : 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: compact ? 9.5 : 10.5,
              fontWeight: 700,
              color: '#9aa3b0',
            }}
          >
            {w}
          </span>
        ))}
      </div>

      {/* day grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7,1fr)',
          rowGap: compact ? 2 : 3,
        }}
      >
        {cells.map((c, i) => (
          <span
            key={c.iso + i}
            role="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!disabled(c.iso)) onSelect(c.iso);
            }}
            onMouseEnter={(e) => {
              if (
                c.inMonth &&
                !disabled(c.iso) &&
                !sameISO(c.iso, selected) &&
                !sameISO(c.iso, rangeStart) &&
                !sameISO(c.iso, rangeEnd) &&
                !isBetween(c.iso, rangeStart, rangeEnd) &&
                c.iso !== today
              ) {
                (e.currentTarget as HTMLElement).style.background = '#eef4fd';
              }
            }}
            onMouseLeave={(e) => {
              const bg = cellStyle(c.iso, c.inMonth, i % 7).background;
              (e.currentTarget as HTMLElement).style.background =
                (bg as string) || 'transparent';
            }}
            style={cellStyle(c.iso, c.inMonth, i % 7)}
          >
            {c.day}
          </span>
        ))}
      </div>

      {footer !== undefined && (
        <div style={{ marginTop: 11, paddingTop: 11, borderTop: '1px solid #eef1f4' }}>
          {footer}
        </div>
      )}
    </div>
  );
};

export default CalendarPanel;
